import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useFormContext } from "react-hook-form";
import moment from "moment";

// Components
import { Input } from "../../../../Utility/FormElements";
import { TooltipMouseTrack } from "../../../../Utility/TooltipMouseTrack";
import { Combobox } from "../../../../Utility/Combobox";

// Hooks
import useDecodeToken from "../../../../../hooks/useDecodeToken";
import useDebounce from "../../../../../hooks/useDebounce";

// Services
import {
  getBarcodeSuggestions,
  getInvoiceSearchSuggestions,
} from "../../../../../services/deliveryService";

// Constants
import {
  minBarcodeLength,
  orderSearchCategories,
  orderSearchCategoriesOrderHistoryCombobox,
  emails,
} from "../../../../../constants";

const checkIfClient = (decodeToken) =>
  Boolean(decodeToken?.ClientId && decodeToken?.ClientId !== 9999);

const searchTermMinLength = 3;

function InvoiceCombobox({
  onCurrentSerchTermChange,
  onSelectOptionsChange,
  onChangeSearchCategory,
}) {
  // Search State
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const debouncedSearchTerm = useDebounce(currentSearchTerm);

  // Search By State (Invoice #, Barcode)
  const [currentSearchCategory, setCurrentSearchCategory] = useState(
    orderSearchCategoriesOrderHistoryCombobox.invoice.value,
  );

  // Suggestions State
  const [tooltipHideEvent, setTooltipHideEvent] = useState(0);

  // Query: Invoice Search Suggestions
  const {
    data: invoiceSearchSuggestionResponse,
    isInitialLoading: isLoadingInvoiceSearchSuggestion,
  } = useQuery({
    queryKey: ["invoiceSearchSuggestions", debouncedSearchTerm.trim()],
    queryFn: ({ signal }) =>
      getInvoiceSearchSuggestions(debouncedSearchTerm.trim(), signal),
    cacheTime: 0,
    staleTime: 0,
    enabled:
      debouncedSearchTerm.length >= searchTermMinLength &&
      currentSearchCategory === orderSearchCategories.invoice.value,
  });

  // Query: Barcode Search Suggestions
  const {
    data: barcodeSearchSuggestionResponse,
    isInitialLoading: isLoadingBarcodeSearchSuggestion,
  } = useQuery({
    queryKey: ["barcodeSearchSuggestions", debouncedSearchTerm.trim()],
    queryFn: ({ signal }) =>
      getBarcodeSuggestions(debouncedSearchTerm.trim(), signal),
    cacheTime: 0,
    staleTime: 0,
    enabled:
      debouncedSearchTerm.length >= searchTermMinLength &&
      currentSearchCategory === orderSearchCategories.barcode.value,
  });

  const loadingSuggestions =
    isLoadingInvoiceSearchSuggestion || isLoadingBarcodeSearchSuggestion;

  const hideTooltip = () => {
    setTooltipHideEvent((prev) => prev + 1);
  };

  const handleChangeCurrentSearchTerm = (newSearchTerm) => {
    setCurrentSearchTerm(newSearchTerm);
    onCurrentSerchTermChange?.(newSearchTerm);
  };

  const handleChangeCategory = (value) => {
    setCurrentSearchCategory(value);
    onChangeSearchCategory?.(value);

    setSelectedOptions([]);
    onSelectOptionsChange?.([]);

    setCurrentSearchTerm("");
    onCurrentSerchTermChange?.("");
  };

  const setNewSelectedOptions = (options) => {
    setSelectedOptions(options);
    onSelectOptionsChange?.(options);
  };

  return (
    <TooltipMouseTrack
      message={
        <>
          Only 1 {currentSearchCategory.toLowerCase()} per order needed. <br />
          Disregards date range.
        </>
      }
      hideEvent={tooltipHideEvent}
      cooldown={2000}
    >
      <Combobox
        multiselect
        autofilterTrim
        trimOnSelect
        loading={loadingSuggestions}
        categories={Object.values(orderSearchCategoriesOrderHistoryCombobox)}
        options={
          currentSearchCategory === orderSearchCategories.invoice.value
            ? invoiceSearchSuggestionResponse?.data || []
            : barcodeSearchSuggestionResponse?.data || []
        }
        selectedOptions={selectedOptions}
        setSelectedOptions={setNewSelectedOptions}
        currentSearchTerm={currentSearchTerm}
        setCurrentSearchTerm={handleChangeCurrentSearchTerm}
        currentCategory={currentSearchCategory}
        setCurrentCategory={handleChangeCategory}
        onClickComboboxInput={hideTooltip}
        noResultsMessage={
          loadingSuggestions
            ? "Loading Suggestions"
            : currentSearchTerm.length >= searchTermMinLength
              ? currentSearchCategory === orderSearchCategories.invoice.value
                ? "No Suggestions Found In The Last 60 Days"
                : "No Suggestions Found"
              : `Start typing to load suggestions (min. ${searchTermMinLength} characters)`
        }
      />
    </TooltipMouseTrack>
  );
}
const maxDateRangeForClient = 365;
function OrderHistorySearch({ handleOnSubmitOrdersSearch, isLoading }) {
  const [decodedToken] = useDecodeToken();
  const isClient = useMemo(() => checkIfClient(decodedToken), [decodedToken]);

  // Invoice AND Barcode search states
  const [currentSearchTerm, setCurrentSearchTerm] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [currentSearchCategory, setCurrentSearchCategory] = useState(
    orderSearchCategoriesOrderHistoryCombobox.invoice.value,
  );

  const {
    formState: { errors },
    register,
    handleSubmit,
  } = useFormContext();

  const onSubmit = (data) => {
    // Prevent multiple submissions: SPAMMING THE BUTTON
    if (isLoading) return;

    let selectedOptionsAndSearchTerm;
    if (
      currentSearchCategory ===
      orderSearchCategoriesOrderHistoryCombobox.barcode.value
    ) {
      if (
        selectedOptions.length === 0 &&
        currentSearchTerm.length > 0 &&
        currentSearchTerm.length < minBarcodeLength
      ) {
        toast.warn(
          `Barcodes shorter than 8 characters are not currently supported. Please contact ${emails.supportEmail} for assistance.`,
        );
        return;
      }
      selectedOptionsAndSearchTerm =
        currentSearchTerm.length >= minBarcodeLength //probably redundant as we kick out with a warning if this is not the case above
          ? [...selectedOptions, currentSearchTerm]
          : selectedOptions;
    } else {
      selectedOptionsAndSearchTerm =
        currentSearchTerm.length > 0
          ? [...selectedOptions, currentSearchTerm]
          : selectedOptions;
    }

    handleOnSubmitOrdersSearch?.(
      selectedOptions.length > 0 || currentSearchTerm
        ? currentSearchCategory
        : orderSearchCategories.dateRange.value,
      selectedOptionsAndSearchTerm,
      data?.fromDate,
      data?.toDate,
    );
  };

  return (
    <div className="tw-grid tw-gap-y-2.5 lg:tw-grid-cols-12 lg:tw-gap-x-4 xl:tw-gap-x-10 lg:tw-items-end">
      <div className="lg:tw-col-span-5 xl:tw-col-span-3">
        {isClient && (
          <div className="tw-grid lg:tw-items-end lg:tw-grid-cols-12 ">
            <div className="lg:tw-col-span-10 xl:tw-col-span-11">
              <InvoiceCombobox
                onCurrentSerchTermChange={setCurrentSearchTerm}
                onSelectOptionsChange={setSelectedOptions}
                onChangeSearchCategory={setCurrentSearchCategory}
              />
            </div>

            <div className="tw-block tw-text-center tw-mt-2 tw-h-full tw-font-semibold tw-text-xl lg:tw-col-span-2 lg:tw-self-center lg:mt-0 xl:tw-pl-4 xl:tw-col-span-1 xl:tw-text-right">
              or
            </div>
          </div>
        )}
      </div>

      <form className="tw-contents" onSubmit={handleSubmit(onSubmit)}>
        <div className="lg:tw-col-span-3">
          <Input
            label="Created From"
            type="date"
            id="fromDate"
            {...register("fromDate", {
              required: "From Date is required",
              disabled:
                currentSearchTerm.trim().length > 0 ||
                selectedOptions.length > 0,
              validate: {
                validateFromDate: (currentFromDate, { toDate }) => {
                  const from = moment(currentFromDate);
                  const to = moment(toDate);
                  if (to.isBefore(from)) {
                    return "From Date cannot be after To Date";
                  }
                },
                dateRangeLimit: (currentFromDate, { toDate }) => {
                  // For Clients, date range is 60, For non-clients, date range is 14
                  const from = moment(currentFromDate);
                  const to = moment(toDate);
                  const maxRange = isClient ? maxDateRangeForClient : 14;
                  if (to.diff(from, "days") > maxRange) {
                    return `Date range cannot be over ${maxRange} days`;
                  }
                },
              },
            })}
            isError={!!errors.fromDate}
            helperText={
              <div className="lg:tw-hidden lg:tw-min-h-0">
                {errors.fromDate?.message}
              </div>
            }
            helperTextProps={{
              className: "lg:tw-mt-0",
            }}
          />
        </div>

        <div className="lg:tw-col-span-3">
          <Input
            label="Created To"
            type="date"
            {...register("toDate", {
              required: "To Date is required",
              disabled:
                currentSearchTerm.trim().length > 0 ||
                selectedOptions.length > 0,
              validate: {
                validateToDate: (currentToDate, { fromDate }) => {
                  const from = moment(fromDate);
                  const to = moment(currentToDate);
                  if (to.isBefore(from)) {
                    return "To date is before from date";
                  }
                },
                futureLimit: (currentToDate) => {
                  // cannot be more than 7 days in the future
                  const to = moment(currentToDate);
                  const nowPlus7 = moment().add(7, "days");

                  if (to.isAfter(nowPlus7)) {
                    return "To Date cannot be more than 7 days in the future";
                  }
                },
                dateRangeLimit: (currentToDate, { fromDate }) => {
                  // For Clients, date range is 400, For non-clients, date range is 14
                  const from = moment(fromDate);
                  const to = moment(currentToDate);
                  const maxRange = isClient ? maxDateRangeForClient : 14;
                  if (to.diff(from, "days") > maxRange) {
                    return `Date range cannot be over ${maxRange} days`;
                  }
                },
              },
            })}
            isError={!!errors.toDate}
            helperText={
              <div className="lg:tw-hidden lg:tw-min-h-0">
                {errors.toDate?.message}
              </div>
            }
            helperTextProps={{
              className: "lg:tw-mt-0",
            }}
          />
        </div>

        <div className="tw-text-center lg:tw-col-span-1 xl:tw-text-left xl:tw-col-span-3">
          <button
            className="btn btn-primary"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Searching..." : "Search"}
          </button>
        </div>

        <div className="lg:tw-col-span-3" />

        <p className="tw-hidden tw-text-red-500 lg:tw-col-span-3 lg:tw-block">
          {errors?.fromDate?.message}
        </p>

        <p className="tw-hidden tw-text-red-500 lg:tw-col-span-3 lg:tw-block">
          {errors?.toDate?.message}
        </p>
      </form>
    </div>
  );
}

export default OrderHistorySearch;
